import React, { useEffect, useMemo, useState } from 'react'
import { useAppSelector } from '../../../../store/hooks'
import {
  selectCrossClientResultExecutionLoading,
  selectCrossClientResults,
  selectCrossClientResultsExecution,
  selectCrossClientUnableToMatchResultCount,
} from '../../../../store/crossClient/crossClientResults'
import { CrossClientProgress } from '../../Progress/CrossClientProgress'
import { CrossClientClauses } from '../../Clauses/CrossClientClauses'
import { CrossClientQueryPhases, CrossClientQueryResultMatches } from '../../../../models/CrossClient'
import { Divider } from '../../../../components/Divider/Divider'
import { ReactComponent as MatchIcon } from '../../../../assets/images/cross-client-match.svg'
import { ReactComponent as NoMatchIcon } from '../../../../assets/images/cross-client-no-match.svg'
import { ReactComponent as UnableToMatchIcon } from '../../../../assets/images/cross-client-unable-to-match.svg'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import { getFormattedDurationShort } from '../../../../common/duration'

export const CrossClientSidebar: React.FC = () => {
  const execution = useAppSelector(selectCrossClientResultsExecution)
  const results = useAppSelector(selectCrossClientResults)
  const executionLoading = useAppSelector(selectCrossClientResultExecutionLoading)
  const unableToMatchResults = useAppSelector(selectCrossClientUnableToMatchResultCount)

  const [now, setNow] = useState<Date>(new Date())

  const isRunning = execution?.phase === CrossClientQueryPhases.PROCESSING

  const matchingResults = useMemo(() => results.filter((result) => result.match === CrossClientQueryResultMatches.MATCH).length, [results])

  useEffect(() => {
    if (isRunning) {
      const interval = setInterval(() => setNow(new Date()), 1000)
      return () => {
        clearInterval(interval)
      }
    }
  }, [isRunning])

  if (executionLoading || !execution) {
    return <div className='skeleton w-100 h-25' />
  }

  const totalResults = execution.clientIds.length
  const atomicClauses = execution.decomposition?.atomicClauses ?? []
  const notMatchingResults = results.length - matchingResults
  const processedResults = matchingResults + notMatchingResults + unableToMatchResults

  return (
    <div className='d-flex flex-column gap-3 zocks-p-2.0 zocks-bg-color-gray-100 h-100'>
      <div className='d-flex flex-column gap-3 px-3 py-4 border border-1 zocks-border-color-gray-200 zocks-border-1.0 bg-white'>
        <div className='d-flex gap-3'>
          <CrossClientProgress className='cross-client-progress-chart-container' total={totalResults} done={processedResults} showPercentage />
          <div className='d-flex flex-column gap-1 justify-content-center'>
            <div className='paragraph-medium'>
              Checked <strong>{processedResults}</strong> of {execution.clientIds.length.toLocaleString()} clients
            </div>
            {isRunning ? (
              <div className='paragraph-small zocks-text-gray-700'>Running {getFormattedDurationShort(execution.updatedAt, now.toISOString())}</div>
            ) : null}
          </div>
        </div>
        <Divider />
        <div className='d-flex flex-column gap-2'>
          <div className='paragraph-medium medium'>Results overview:</div>
          <div className='d-flex gap-2 align-items-center'>
            <MatchIcon width={12} height={12} />
            <span>
              <span className='medium'>{matchingResults} </span>
              matched
            </span>
          </div>
          <div className='d-flex gap-2 align-items-center'>
            <NoMatchIcon width={12} height={12} />
            <span>
              <span className='medium'>{notMatchingResults} </span>
              didn't match
            </span>
          </div>
          <div className='d-flex gap-2 align-items-center'>
            <UnableToMatchIcon />
            <span>
              <span className='medium'>{unableToMatchResults} </span>
              couldn't be evaluated
              <Tooltip className='position-fixed' title="These clients couldn't be evaluated due to missing data, or incomplete records.">
                <i className='bi bi-info-square fs-6 zocks-text-gray-700 ps-1' />
              </Tooltip>
            </span>
          </div>
        </div>
        {totalResults !== processedResults ? (
          <div className='d-flex zocks-gap-0.75 zocks-p-0.75 zocks-bg-color-gray-100 border border-1 zocks-border-color-gray-200 zocks-border-0.5 align-items-center'>
            <i className='bi bi-lightbulb fs-6' />
            <div className='paragraph-small'>
              <span className='medium'>Tip:</span> Feel free to close this page. Your search will keep running in the background.
            </div>
          </div>
        ) : null}
      </div>
      <div className='d-flex flex-column gap-3 px-3 py-4 border border-1 zocks-border-color-gray-200 zocks-border-1.0 bg-white'>
        <h6>Your search</h6>
        <div className='paragraph-medium'>{execution.userQuestion}</div>
        <Divider />
        <div className='paragraph-medium'>Checking clients against these criteria:</div>
        <CrossClientClauses atomicClauses={atomicClauses} />
      </div>
    </div>
  )
}
